/**
 * Upload size limits, surfaced in the UI.
 *
 * These MUST stay in step with two server-side limits, which are enforced in this order:
 *
 *   1. nginx  client_max_body_size   .platform/nginx/conf.d/proxy.conf
 *   2. multer limits.fileSize        backend/src/config/upload.js
 *
 * nginx is the one that bites first, and it rejects the request before Express runs — so
 * an oversized file comes back as an HTML 413 rather than a JSON API error, which the app
 * can only report as a generic "server error". Telling people the limit up front is the
 * difference between a clear constraint and an unexplained failure.
 *
 * If you raise a limit, raise it in all three places or the smallest silently wins.
 */

/** Session workbooks and pre-read materials — multer `uploadSessionFile`. */
export const MAX_UPLOAD_MB = 50;

/** Session imagery — multer `uploadSessionImage`. */
export const MAX_IMAGE_UPLOAD_MB = 10;

/** Simulation .docx/.xlsx import and AI reference documents — backend/src/routes/simulation.js. */
export const MAX_SIMULATION_UPLOAD_MB = 15;

/** Profile pictures — multer `uploadProfile`. */
export const MAX_AVATAR_UPLOAD_MB = 5;

/**
 * True when a file exceeds the limit. Lets a caller fail fast with a precise message
 * instead of spending the upload and getting an opaque 413 back.
 */
export const isOverLimit = (file, limitMb = MAX_UPLOAD_MB) =>
  Boolean(file) && file.size > limitMb * 1024 * 1024;

/** "12.4 MB" — for telling someone how far over they are. */
export const formatFileSize = (bytes) => {
  if (!Number.isFinite(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
